<template>
  <SidebarComponent />
  <div class="dashboard-layout" :class="{ 'user-details-layout': isUserDetailsPage }">
    <div class="content-container">
      <div class="card-container user-layout-card">
        <UserNavigation :userId="resolvedUserId" />
        <div class="user-layout-content">
          <router-view :key="resolvedUserId" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import SidebarComponent from '@/components/SidebarComponent.vue';
import UserNavigation from '@/components/UserNavigation.vue';

export default {
  components: {
    SidebarComponent,
    UserNavigation,
  },
  props: {
    userId: {
      type: String,
      default: '',
    },
  },
  setup(props) {
    const route = useRoute();
    const resolvedUserId = computed(() => props.userId || route.params.userId || '');
    const isUserDetailsPage = computed(() => route.name === 'UserDetails');

    return {
      resolvedUserId,
      isUserDetailsPage,
    };
  },
};
</script>

<style scoped>
.user-layout-card {
  margin-top: 0;
  padding-right: 30px;
}

.user-layout-content {
  margin-top: 24px;
}

@media (max-width: 1024px) {
  .user-layout-card {
    padding-right: 16px;
  }

  .user-layout-content {
    margin-top: 18px;
  }
}

/* Keep the profile shell responsive without changing the other user tabs. */
.user-details-layout {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  margin: 0;
}

.user-details-layout .content-container {
  box-sizing: border-box;
  flex: 1 1 auto;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  margin: 0;
  padding: 82px 24px 32px var(--staff-content-inset, 252px);
}

.user-details-layout .content-container.sidebar-hidden {
  padding-left: 30px;
}

.user-details-layout .user-layout-card,
.user-details-layout .user-layout-content {
  box-sizing: border-box;
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.user-details-layout .user-layout-card {
  margin: 0;
  padding: 0;
}

.user-details-layout .user-layout-content {
  margin-top: 22px;
}

.user-details-layout :deep(.user-nav-wrapper),
.user-details-layout :deep(.user-info-bar),
.user-details-layout :deep(.info-row),
.user-details-layout :deep(.user-nav) {
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
}

.user-details-layout :deep(.user-info-bar) {
  margin-top: 0;
  padding: 12px;
}

.user-details-layout :deep(.info-row) {
  align-items: stretch;
  gap: 8px;
}

.user-details-layout :deep(.info-chip) {
  box-sizing: border-box;
  flex: 1 1 120px;
  min-width: 0;
  max-width: 100%;
  gap: 3px;
}

.user-details-layout :deep(.info-chip strong) {
  min-width: 0;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.user-details-layout :deep(.info-chip--email) {
  flex-basis: 180px;
}

.user-details-layout :deep(.alert-chip) {
  min-width: 0;
  max-width: 100%;
  overflow-wrap: anywhere;
}

.user-details-layout :deep(.info-action) {
  box-sizing: border-box;
  align-self: center;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  min-height: 40px;
  margin: 0;
}

.user-details-layout :deep(.order-badge) {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
}

.user-details-layout :deep(.credit-button) {
  width: auto;
  flex-wrap: wrap;
  justify-content: center;
}

.user-details-layout :deep(.user-nav ul) {
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
  margin: 0;
}

.user-details-layout :deep(.user-nav .nav-item) {
  box-sizing: border-box;
  min-width: 44px;
  min-height: 44px;
  max-width: 100%;
  margin: 0;
  padding: 8px 10px;
  gap: 5px;
  justify-content: center;
  line-height: 1.25;
  overflow-wrap: anywhere;
}

@media (max-width: 768px) {
  .user-details-layout .content-container,
  .user-details-layout .content-container.sidebar-hidden {
    margin: 0;
    padding: 72px 12px 24px;
  }

  .user-details-layout .user-layout-content {
    margin-top: 18px;
  }

  .user-details-layout :deep(.user-nav-wrapper) {
    gap: 10px;
  }

  .user-details-layout :deep(.user-info-bar) {
    padding: 10px;
  }
}

@media (max-width: 600px) {
  .user-details-layout :deep(.info-chip--email) {
    flex-basis: 100%;
  }

  .user-details-layout :deep(.user-nav ul),
  .user-details-layout :deep(.user-nav:has(.allenamenti-nav-item) ul) {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    flex-wrap: nowrap;
    padding-bottom: 6px;
    overflow-x: auto;
    overflow-y: hidden;
    overscroll-behavior-x: contain;
    scrollbar-width: thin;
    scrollbar-color: #c4c4c4 transparent;
    -webkit-overflow-scrolling: touch;
  }

  .user-details-layout :deep(.user-nav .nav-item) {
    flex: 0 0 auto;
    white-space: nowrap;
  }

  .user-details-layout :deep(.user-nav ul::-webkit-scrollbar) {
    height: 4px;
  }

  .user-details-layout :deep(.user-nav ul::-webkit-scrollbar-thumb) {
    border-radius: 4px;
    background: #c4c4c4;
  }

  .user-details-layout :deep(.user-nav)::after {
    display: block;
    margin-top: 4px;
    color: #737373;
    font-size: 11px;
    line-height: 1.4;
    content: 'Scorri le sezioni';
  }
}
</style>
